/**
 * TypologyDefaultsWarning — V14.63 Sprint 28C.
 *
 * Compact banner that lists which generation params will use smart defaults
 * because the user left them empty. Calls the backend endpoint
 * `/api/prompt-config/typology-defaults` to compute the diff between the
 * caller-provided params and the typology-aware defaults that would be
 * injected at generation time.
 *
 * UX :
 *   - Hidden when 0 fields are defaulted.
 *   - When 1-3 fields : amber pill ("3 champs utiliseront des valeurs par défaut").
 *   - When 4+ fields : amber section with bullet list + "voir détail".
 *   - The user can dismiss the banner ("OK compris") or click "Pré-remplir
 *     les champs" to instantly populate the form with patched defaults.
 *
 * Props :
 *   - params : the GenerationParams flat dict the user is editing
 *   - projectType : "apartment" | "house_single" | "residence" | ...
 *   - onApplyDefaults?: (patchedParams) => void — called when the user clicks
 *                       "Pré-remplir" so the parent form can update its state.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Sparkles, AlertCircle, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TypologyDefaultsWarning = ({
  params,
  projectType,
  onApplyDefaults,
  className = '',
  testId = 'typology-defaults-warning',
}) => {
  const [diff, setDiff] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const abortRef = useRef(null);

  // Debounced fetch — re-runs when params change (the user types) and
  // when projectType changes. 400 ms debounce so we don't hammer the API
  // on every keystroke.
  // Deep-compare the dict by JSON-stringifying ; cheap enough at this scale.
  const sig = useMemo(() => JSON.stringify({ params: params || {}, projectType }),
    [params, projectType]);

  useEffect(() => {
    if (!params || !projectType) return undefined;
    const handle = setTimeout(() => {
      if (abortRef.current) abortRef.current.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setLoading(true);
      axios.post(
        `${API}/prompt-config/typology-defaults`,
        { params, project_type: projectType },
        { withCredentials: true, signal: ctrl.signal },
      ).then((r) => setDiff(r.data))
        .catch(() => { /* silent — banner just hides */ })
        .finally(() => setLoading(false));
    }, 400);
    return () => clearTimeout(handle);
  }, [sig]);

  if (dismissed || !diff || (diff.defaulted_fields || []).length === 0) {
    return null;
  }

  const count = diff.defaulted_fields.length;
  const isCompact = count <= 3;

  return (
    <section
      data-testid={testId}
      className={`rounded-lg border p-3 flex items-start gap-3 ${className}`}
      style={{
        borderColor: 'rgba(251, 191, 36, 0.4)',
        background: 'linear-gradient(135deg, rgba(120, 53, 15, 0.18), rgba(2, 6, 12, 0.6))',
      }}
    >
      <Sparkles className="w-4 h-4 shrink-0 mt-0.5" style={{ color: '#fbbf24' }} />
      <div className="flex-1 min-w-0">
        <div className="text-[11.5px] font-semibold text-amber-200 inline-flex items-center gap-1.5">
          {isCompact
            ? `${count} champ${count > 1 ? 's utiliseront' : ' utilisera'} des valeurs par défaut`
            : `${count} champs utiliseront des valeurs par défaut (typologie : ${diff.project_type})`}
          {loading && <span className="text-[10px] text-amber-200/60 italic">recalcul…</span>}
        </div>
        {isCompact ? (
          <div className="text-[10.5px] text-gray-300 mt-1" data-testid="typology-defaults-summary">
            {diff.defaults_summary}
          </div>
        ) : (
          <details className="mt-1">
            <summary className="text-[10.5px] text-amber-200/80 cursor-pointer hover:text-amber-100 select-none">
              Voir le détail des {count} valeurs par défaut
            </summary>
            <ul className="text-[10.5px] text-gray-300 mt-1.5 space-y-0.5 pl-3 list-disc"
                data-testid="typology-defaults-list">
              {diff.defaulted_fields.map((field) => (
                <li key={field}>
                  <span className="font-mono text-amber-200/80">{field}</span>
                  <span className="text-gray-400"> → </span>
                  <span>{String(diff.patched_params?.[field] || '').slice(0, 90)}</span>
                </li>
              ))}
            </ul>
          </details>
        )}
        <div className="mt-2 flex items-center gap-2">
          {onApplyDefaults && (
            <button
              type="button"
              onClick={() => onApplyDefaults(diff.patched_params || {})}
              data-testid="typology-defaults-apply-btn"
              className="inline-flex items-center gap-1 h-6 px-2 rounded text-[10.5px] font-semibold bg-amber-500 hover:bg-amber-400 text-amber-950"
            >
              <Check className="w-3 h-3" />
              Pré-remplir les champs
            </button>
          )}
          <button
            type="button"
            onClick={() => setDismissed(true)}
            data-testid="typology-defaults-dismiss-btn"
            className="inline-flex items-center gap-1 h-6 px-2 rounded text-[10.5px] border border-white/15 text-gray-300 hover:bg-white/5"
          >
            <AlertCircle className="w-3 h-3" />
            OK, compris
          </button>
        </div>
      </div>
    </section>
  );
};

export default TypologyDefaultsWarning;
